import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/authContext';
import { getBaseById, cookNow } from '../services/api';
import { Section, Heading, Text } from '../components/ui';

const CookNow = () => {
    const { kernelId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    
    const [kernel, setKernel] = useState(null);
    const [loading, setLoading] = useState(true);
    const [generating, setGenerating] = useState(false);
    const [error, setError] = useState(null);
    
    // Form state
    const [goal, setGoal] = useState('');
    const [pantry, setPantry] = useState('');
    const [timeMins, setTimeMins] = useState(30);

    useEffect(() => {
        if (!user) {
            navigate('/login');
            return;
        }
        
        fetchKernel();
    }, [kernelId, user, navigate]);

    const fetchKernel = async () => {
        try {
            setLoading(true);
            // Get token if user is authenticated, otherwise try without token
            let token = null;
            if (user) {
                token = await user.getIdToken();
            }
            const kernelData = await getBaseById(kernelId, token);
            setKernel(kernelData);
        } catch (err) {
            console.error('Error fetching kernel:', err);
            setError('Failed to load cooking technique. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleStartCooking = async (e) => {
        e.preventDefault();
        
        if (!goal.trim()) {
            setError('Please describe your meal goal');
            return;
        }

        try {
            setGenerating(true);
            setError(null);
            
            const token = await user.getIdToken();
            const result = await cookNow(kernelId, goal, pantry, timeMins, token);
            
            // Redirect to live session
            navigate(`/live/${result.draftId}`);
        } catch (err) {
            console.error('Error starting cook session:', err);
            setError(err.message || 'Failed to start cooking session. Please try again.');
        } finally {
            setGenerating(false);
        }
    };

    if (loading) {
        return (
            <Section className="w-11/12 md:w-2/3 lg:w-1/2 mx-auto">
                <Heading level={2}>Loading cooking technique...</Heading>
            </Section>
        );
    }

    if (error && !kernel) {
        return (
            <Section className="w-11/12 md:w-2/3 lg:w-1/2 mx-auto">
                <Heading level={2}>Error</Heading>
                <Text>{error}</Text>
                <button 
                    onClick={() => navigate('/bases')}
                    className="btn-small mt-4"
                >
                    ← Back to Techniques
                </button>
            </Section>
        );
    }

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <Section className="w-11/12 md:w-2/3 lg:w-1/2 max-h-screen overflow-y-auto">
                <div className="flex justify-between items-start mb-6">
                    <Heading level={2}>Cook Now: {kernel?.title}</Heading>
                    <button 
                        onClick={() => navigate(-1)}
                        className="text-2xl font-bold hover:opacity-60 transition-opacity"
                    >
                        ✕
                    </button>
                </div>

                {kernel && (
                    <div className="mb-6">
                        <Text className="opacity-60 mb-4">
                            Get a personalized recipe for {kernel.title.toLowerCase()} based on your preferences.
                        </Text>
                        
                        <div className="bg-neutral-50 p-4 hairline mb-6">
                            <Text size="sm" className="font-mono">
                                <strong>Technique Slots:</strong> {Object.keys(kernel.slots).join(', ')}
                            </Text>
                        </div>
                    </div>
                )}

                <form onSubmit={handleStartCooking} className="space-y-6">
                    {/* Meal Goal */}
                    <div>
                        <label className="block mb-2">
                            <Text className="font-medium">Describe your meal goal</Text>
                        </label>
                        <textarea
                            value={goal}
                            onChange={(e) => setGoal(e.target.value)}
                            placeholder="e.g., A quick weeknight dinner for two, comfort food for a rainy day, healthy lunch..."
                            className="w-full p-3 hairline bg-white resize-none"
                            rows={3}
                            required
                        />
                    </div>

                    {/* Pantry */}
                    <div>
                        <label className="block mb-2">
                            <Text className="font-medium">What's in your pantry?</Text>
                        </label>
                        <textarea
                            value={pantry}
                            onChange={(e) => setPantry(e.target.value)}
                            placeholder="e.g., chicken breast, bell peppers, onions, garlic, olive oil, rice..."
                            className="w-full p-3 hairline bg-white resize-none"
                            rows={3}
                        />
                        <Text size="sm" className="opacity-60 mt-1">
                            List ingredients you have available (comma-separated)
                        </Text>
                    </div>

                    {/* Time Budget */}
                    <div>
                        <label className="block mb-2">
                            <Text className="font-medium">How much time do you have?</Text>
                        </label>
                        <div className="space-y-3">
                            <input
                                type="range"
                                min="10"
                                max="120"
                                step="5"
                                value={timeMins}
                                onChange={(e) => setTimeMins(parseInt(e.target.value))}
                                className="custom-slider"
                            />
                            <div className="flex justify-between text-xs font-mono text-neutral-900 opacity-60">
                                <span>10 MIN</span>
                                <span>25 MIN</span>
                                <span>40 MIN</span>
                                <span>55 MIN</span>
                                <span>70 MIN</span>
                                <span>85 MIN</span>
                                <span>100 MIN</span>
                                <span>120 MIN</span>
                            </div>
                            <div className="text-center">
                                <span className="text-sm font-mono font-bold bg-neutral-50 px-3 py-1 hairline">{timeMins} minutes</span>
                            </div>
                        </div>
                    </div>

                    {error && (
                        <div className="bg-red-50 p-4 hairline">
                            <Text className="text-red-900">{error}</Text>
                        </div>
                    )}

                    {/* Actions */}
                    <div className="flex gap-3 pt-4 hairline-t">
                        <button
                            type="button"
                            onClick={() => navigate(-1)}
                            className="btn-small btn-secondary flex-1"
                            disabled={generating}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="btn-small flex-1"
                            disabled={generating || !goal.trim()}
                        >
                            {generating ? (
                                <>
                                    <span className="animate-spin inline-block mr-2">⚡</span>
                                    Generating...
                                </>
                            ) : (
                                'Start Cooking'
                            )}
                        </button>
                    </div>
                </form>

                {generating && (
                    <div className="mt-6 bg-neutral-50 p-4 hairline">
                        <Text size="sm" className="opacity-60">
                            Creating your personalized recipe... This may take a few seconds.
                        </Text>
                    </div>
                )}
            </Section>
        </div>
    );
};

export default CookNow; 
